import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useSandboxV2Applications } from '../features/sandboxes/hooks/useSandboxesV2';
import {
  applicationSandboxQueryOptions,
  type ApplicationSandbox,
  type SandboxV2DeclaredAction,
  type SandboxV2Operation,
} from './useApplicationSandbox';

/**
 * What the sandbox_action node picker offers: a script action a Sandboxes v2
 * definition declares, or an emulator operation (`<plugin>.<op>`). Both are
 * shaped as a JSON-Schema-like input and output so the picker renders them alike.
 * `outputSchema` keeps only primitive properties: the executor registers only
 * those as `$var.*`.
 */
export type AvailableSandboxAction = {
  slug: string;
  description: string;
  kind: 'action' | 'operation';
  inputSchema: {
    properties: Record<string, { type: string; description?: string; default?: unknown; sensitive?: boolean }>;
    required: string[];
  };
  outputKeys: string[];
};

// Same exclusion as the API's FAST_FORWARD builtin (packages/sandbox-helper/src/manifest.ts).
const DATABASE_FAST_FORWARD = 'database.fast-forward';
const PRIMITIVE_OUTPUT_TYPES = new Set(['string', 'number', 'integer', 'boolean']);

function primitiveKeys(properties: Record<string, { type?: string }> | undefined): string[] {
  return Object.entries(properties ?? {})
    .filter(([, prop]) => prop.type !== undefined && PRIMITIVE_OUTPUT_TYPES.has(prop.type))
    .map(([key]) => key);
}

function fromDeclaredAction(action: SandboxV2DeclaredAction): AvailableSandboxAction {
  const properties: AvailableSandboxAction['inputSchema']['properties'] = {};
  const required: string[] = [];
  for (const [key, prop] of Object.entries(action.input)) {
    properties[key] = { type: prop.type, description: prop.description, default: prop.default, sensitive: prop.sensitive };
    if (prop.required) required.push(key);
  }
  return {
    slug: action.slug,
    description: action.description,
    kind: 'action',
    inputSchema: { properties, required },
    outputKeys: primitiveKeys(action.output),
  };
}

function fromOperation(operation: SandboxV2Operation): AvailableSandboxAction {
  const properties: AvailableSandboxAction['inputSchema']['properties'] = {};
  for (const [key, prop] of Object.entries(operation.inputSchema.properties ?? {})) {
    properties[key] = { type: prop.type, description: prop.description, default: prop.default };
  }
  return {
    slug: operation.name,
    description: operation.description,
    kind: 'operation',
    inputSchema: { properties, required: operation.inputSchema.required ?? [] },
    outputKeys: primitiveKeys(operation.outputSchema.properties),
  };
}

/** Module-level so `useQueries` can memoize the combined result across renders. */
function combineActions(results: UseQueryResult<ApplicationSandbox>[]): {
  actions: AvailableSandboxAction[];
  isLoading: boolean;
} {
  const bySlug = new Map<string, AvailableSandboxAction>();
  const add = (action: AvailableSandboxAction) => {
    if (!bySlug.has(action.slug)) bySlug.set(action.slug, action);
  };
  for (const result of results) {
    for (const action of result.data?.definition?.actions ?? []) add(fromDeclaredAction(action));
    for (const operation of result.data?.definition?.operations ?? []) {
      // The sandbox_action executor only runs emulator operations, not the database fast-forward builtin.
      if (operation.name !== DATABASE_FAST_FORWARD) add(fromOperation(operation));
    }
  }
  return { actions: [...bySlug.values()], isLoading: results.some((result) => result.isLoading) };
}

/**
 * Actions and emulator operations declared by the org's Sandboxes v2
 * definitions, for the sandbox_action node picker. It is enabled by default
 * where actions are listed; the canvas enables it only when sandbox_action
 * nodes need sensitivity metadata. An application with no ready version or
 * no sandbox setting simply contributes none. The first declaration of a
 * slug wins.
 */
export function useAvailableSandboxActions({ enabled = true }: { enabled?: boolean } = {}) {
  const applicationsQuery = useSandboxV2Applications();
  const { actions, isLoading } = useQueries({
    queries: (applicationsQuery.data ?? []).map((application) => ({
      ...applicationSandboxQueryOptions(application.id),
      enabled,
    })),
    combine: combineActions,
  });
  return { actions, isLoading: applicationsQuery.isLoading || isLoading };
}
